import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/internal/operators/map';
import { environment } from 'src/environments/environment';

import { IContact, IContactNote, IContactTasks, ILead } from '../../model/contacts.model';
import { ISibasiHTTPResponse } from 'src/app/common/model/sibasiHTTPResponse.model';
import { IPagination } from 'src/app/common/model/util.model';

@Injectable({
  providedIn: 'root'
})
export class ContactsService {
  constructor(private http: HttpClient) {}

  public getAllCustomers(): Observable<ISibasiHTTPResponse<IContact[]>> {
    return this.http.get<ISibasiHTTPResponse<IContact[]>>(
      `${environment.api_url}pos/customer`
    );
  }
  public addCustomers(customer: any): Observable<ISibasiHTTPResponse<IContact>> {
    return this.http.post<ISibasiHTTPResponse<IContact>>(
      `${environment.api_url}pos/customer`,
      customer
    );
  }
  public addBulkCustomers(items: []): Observable<ISibasiHTTPResponse<any>> {
    return this.http.post<ISibasiHTTPResponse<any>>(
      `${environment.api_url}pos/customer/bulk/add`,
      items
    );
  }
  public getCustomerById(customerId: string): Observable<ISibasiHTTPResponse<IContact>> {
    return this.http.get<ISibasiHTTPResponse<IContact>>(
      `${environment.api_url}pos/customer/${customerId}`
    );
  }
  public updateCustomer(
    customerId: string | undefined,
    customer: {}
  ): Observable<ISibasiHTTPResponse<IContact>> {
    return this.http.post<ISibasiHTTPResponse<IContact>>(
      `${environment.api_url}pos/customer/${customerId}`,
      customer
    );
  }
  public deleteCustomer(customerId: string): Observable<ISibasiHTTPResponse<IContact>> {
    return this.http.delete<ISibasiHTTPResponse<IContact>>(
      `${environment.api_url}pos/customer/${customerId}`
    );
  }
  getAllCustomersCount(): Observable<ISibasiHTTPResponse<number>> {
    return this.http.get<ISibasiHTTPResponse<number>>(
      `${environment.api_url}pos/customer/all/count`
    );
  }
  getCustomerNameExistence(
    customerName: string
  ): Observable<ISibasiHTTPResponse<{ message: string; result: boolean }>> {
    return this.http.get<ISibasiHTTPResponse<{ message: string; result: boolean }>>(
      `${environment.api_url}pos/customer/confirmExistence/customer/${customerName}`
    );
  }

  getAllSearchedCustomersCount(
    searchParam: string
  ): Observable<ISibasiHTTPResponse<number>> {
    return this.http.post<ISibasiHTTPResponse<number>>(
      `${environment.api_url}pos/customer/all/count/searched`,
      { searchParam }
    );
  }

  getPaginatedCustomers(
    pagination?: any
  ): Observable<ISibasiHTTPResponse<IPagination<IContact[]>>> {
    return this.http.post<ISibasiHTTPResponse<IPagination<IContact[]>>>(
      `${environment.api_url}pos/customer/all/paginated`,
      pagination ? pagination : {}
    );
  }

  public getContactNotes(): Observable<ISibasiHTTPResponse<IContactNote[]>> {
    return this.http.get<ISibasiHTTPResponse<IContactNote[]>>(
      `${environment.api_url}pos/contact/note/all`
    );
  }
  public addContactNote(contactNote: any): Observable<ISibasiHTTPResponse<IContactNote>> {
    return this.http.post<ISibasiHTTPResponse<IContactNote>>(
      `${environment.api_url}pos/contact/note`,
      contactNote
    );
  }

  public updateContactNote(
    contactNoteId: string,
    contactNote: {}
  ): Observable<ISibasiHTTPResponse<IContactNote>> {
    return this.http.post<ISibasiHTTPResponse<IContactNote>>(
      `${environment.api_url}pos/contact/note/update/${contactNoteId}`,
      contactNote
    );
  }
  public deleteContactNote(noteId: any) {
    return this.http.delete(`${environment.api_url}pos/contact/note/delete/${noteId}`);
  }

  public getContactActionPointsAndTasks(customerId: string): Observable<IContactTasks> {
    return this.http
      .get<
        ISibasiHTTPResponse<IContactTasks>
      >(`${environment.api_url}pos/reports/transactions/history/contact/${customerId}`)
      .pipe(map((x) => x.result));
  }

  getContactHistory(contactId: string): Observable<
    ISibasiHTTPResponse<{
      transactionHistory: any[];
      leadTracks: any[];
      contactNotes: any[];
    }>
  > {
    return this.http.get<
      ISibasiHTTPResponse<{
        transactionHistory: any[];
        leadTracks: any[];
        contactNotes: any[];
      }>
    >(`${environment.api_url}pos/contact/history/${contactId}`);
  }

  getTopBoughtItems(contactId: string | undefined): Observable<ISibasiHTTPResponse<[]>> {
    return this.http.get<ISibasiHTTPResponse<[]>>(
      `${environment.api_url}pos/reports/items/mostBought/topFive/customer/${contactId}`
    );
  }
  getContactOpportunities(
    contactId: string | undefined
  ): Observable<ISibasiHTTPResponse<[]>> {
    return this.http.get<ISibasiHTTPResponse<[]>>(
      `${environment.api_url}contactOpportunity/contact/all/${contactId}`
    );
  }

  addContactOpportunity(opportunity: any): Observable<ISibasiHTTPResponse<{}>> {
    return this.http.post<ISibasiHTTPResponse<{}>>(
      `${environment.api_url}contactOpportunity`,
      opportunity
    );
  }

  getAllPaginatedCustomerNotes(
    pagination?: any
  ): Observable<ISibasiHTTPResponse<IPagination<any[]>>> {
    return this.http.post<ISibasiHTTPResponse<IPagination<any[]>>>(
      `${environment.api_url}pos/customer/all/notes/paginated`,
      pagination ? pagination : {}
    );
  }

  getAllCustomerNotes(): Observable<ISibasiHTTPResponse<any[]>> {
    return this.http.get<ISibasiHTTPResponse<any[]>>(
      `${environment.api_url}pos/customer/all/notes`
    );
  }

  //  *********************************************VENDOR SERVICE*******************************************************
  public addVendor(vendor: any): Observable<ISibasiHTTPResponse<IContact>> {
    return this.http.post<ISibasiHTTPResponse<IContact>>(
      `${environment.api_url}pos/vendor`,
      vendor
    );
  }
  public updateVendor(
    vendorId: string | undefined,
    vendor: {}
  ): Observable<ISibasiHTTPResponse<IContact>> {
    return this.http.post<ISibasiHTTPResponse<IContact>>(
      `${environment.api_url}pos/vendor/${vendorId}`,
      vendor
    );
  }
  public getAllVendors(): Observable<ISibasiHTTPResponse<IContact[]>> {
    return this.http.get<ISibasiHTTPResponse<IContact[]>>(
      `${environment.api_url}pos/vendor`
    );
  }
  public getVendorById(vendorId: string): Observable<ISibasiHTTPResponse<IContact>> {
    return this.http.get<ISibasiHTTPResponse<IContact>>(
      `${environment.api_url}pos/vendor/${vendorId}`
    );
  }
  public deleteVendor(vendorId: any) {
    return this.http.delete(`${environment.api_url}pos/vendor/${vendorId}`);
  }
  getPaginatedVendors(
    pagination?: any
  ): Observable<ISibasiHTTPResponse<IPagination<IContact[]>>> {
    return this.http.post<ISibasiHTTPResponse<IPagination<IContact[]>>>(
      `${environment.api_url}pos/vendor/all/paginated`,
      pagination ? pagination : {}
    );
  }

  getAllVendorsCount(): Observable<ISibasiHTTPResponse<number>> {
    return this.http.get<ISibasiHTTPResponse<number>>(
      `${environment.api_url}pos/vendor/all/count`
    );
  }
  getAllSearchedVendorsCount(
    searchParam: string
  ): Observable<ISibasiHTTPResponse<number>> {
    return this.http.post<ISibasiHTTPResponse<number>>(
      `${environment.api_url}pos/vendor/all/count/searched`,
      { searchParam }
    );
  }

  public addBulkVendors(items: []): Observable<ISibasiHTTPResponse<any>> {
    return this.http.post<ISibasiHTTPResponse<any>>(
      `${environment.api_url}pos/vendor/bulk/add`,
      items
    );
  }

  //  *********************************************LEADS SERVICE*******************************************************

  public getAllLeads(): Observable<ISibasiHTTPResponse<IContact[]>> {
    return this.http.get<ISibasiHTTPResponse<IContact[]>>(
      `${environment.api_url}lead/track`
    );
  }
  public addLead(lead: any): Observable<ISibasiHTTPResponse<IContact>> {
    return this.http.post<ISibasiHTTPResponse<IContact>>(
      `${environment.api_url}lead/track/new`,
      lead
    );
  }
  public getLeadById(
    leadId: string | undefined
  ): Observable<ISibasiHTTPResponse<IContact>> {
    return this.http.get<ISibasiHTTPResponse<IContact>>(
      `${environment.api_url}lead/track/${leadId}`
    );
  }
  public updateLead(
    leadId: string | undefined,
    lead: {}
  ): Observable<ISibasiHTTPResponse<IContact>> {
    return this.http.post<ISibasiHTTPResponse<IContact>>(
      `${environment.api_url}lead/track/${leadId}`,
      lead
    );
  }
  public deleteLead(leadId: string): Observable<ISibasiHTTPResponse<IContact>> {
    return this.http.delete<ISibasiHTTPResponse<IContact>>(
      `${environment.api_url}lead/track/${leadId}`
    );
  }
  getAllLeadsCount(): Observable<ISibasiHTTPResponse<number>> {
    return this.http.get<ISibasiHTTPResponse<number>>(
      `${environment.api_url}lead/track/all/count`
    );
  }
  linkLeadToContact(
    leadId: string | undefined,
    contactId: string | undefined
  ): Observable<ISibasiHTTPResponse<ILead>> {
    return this.http.get<ISibasiHTTPResponse<ILead>>(
      `${environment.api_url}lead/track/link/${leadId}/${contactId}`
    );
  }

  getAllSearchedLeadsCount(searchParam: string): Observable<ISibasiHTTPResponse<number>> {
    return this.http.post<ISibasiHTTPResponse<number>>(
      `${environment.api_url}lead/track/all/count/searched`,
      { searchParam }
    );
  }

  getPaginatedLeads(
    pagination?: any
  ): Observable<ISibasiHTTPResponse<IPagination<IContact[]>>> {
    return this.http.post<ISibasiHTTPResponse<IPagination<IContact[]>>>(
      `${environment.api_url}lead/track/all/paginated`,
      pagination ? pagination : {}
    );
  }

  public addBulkLeads(items: []): Observable<ISibasiHTTPResponse<any>> {
    return this.http.post<ISibasiHTTPResponse<any>>(
      `${environment.api_url}lead/track/bulk/add`,
      items
    );
  }

  convertLeadToCustomer(
    leadId: string | undefined
  ): Observable<ISibasiHTTPResponse<IContact>> {
    return this.http.get<ISibasiHTTPResponse<IContact>>(
      `${environment.api_url}lead/track/convert/customer/${leadId}`
    );
  }

  //  *********************************************EMPLOYEE SERVICE*******************************************************

  public addEmployee(employee: any): Observable<ISibasiHTTPResponse<IContact>> {
    return this.http.post<ISibasiHTTPResponse<IContact>>(
      `${environment.api_url}employees`,
      employee
    );
  }
  public updateEmployee(
    employeeId: string | undefined,
    employee: {}
  ): Observable<ISibasiHTTPResponse<IContact>> {
    return this.http.post<ISibasiHTTPResponse<IContact>>(
      `${environment.api_url}employees/${employeeId}`,
      employee
    );
  }
  public getAllEmployees(): Observable<ISibasiHTTPResponse<IContact[]>> {
    return this.http.get<ISibasiHTTPResponse<IContact[]>>(
      `${environment.api_url}employees`
    );
  }
  public getEmployeeById(employeeId: string): Observable<ISibasiHTTPResponse<IContact>> {
    return this.http.get<ISibasiHTTPResponse<IContact>>(
      `${environment.api_url}employees/${employeeId}`
    );
  }
  public deleteEmployee(employeeId: any) {
    return this.http.delete(`${environment.api_url}employees/${employeeId}`);
  }
  getPaginatedEmployees(
    pagination?: any
  ): Observable<ISibasiHTTPResponse<IPagination<IContact[]>>> {
    return this.http.post<ISibasiHTTPResponse<IPagination<IContact[]>>>(
      `${environment.api_url}employees/all/paginated`,
      pagination ? pagination : {}
    );
  }

  getAllEmployeesCount(): Observable<ISibasiHTTPResponse<number>> {
    return this.http.get<ISibasiHTTPResponse<number>>(
      `${environment.api_url}employees/all/count`
    );
  }
  getAllSearchedEmployeesCount(
    searchParam: string
  ): Observable<ISibasiHTTPResponse<number>> {
    return this.http.post<ISibasiHTTPResponse<number>>(
      `${environment.api_url}employees/all/count/searched`,
      { searchParam }
    );
  }

  public addBulkEmployees(items: []): Observable<ISibasiHTTPResponse<any>> {
    return this.http.post<ISibasiHTTPResponse<any>>(
      `${environment.api_url}employees/bulk/add`,
      items
    );
  }

  public createEmployeeFromUserProfile(user: {
    email: string;
    userIdToMatch: string;
  }): Observable<ISibasiHTTPResponse<IContact>> {
    return this.http.post<ISibasiHTTPResponse<IContact>>(
      `${environment.api_url}employees/createEmployeeFromUserDetails`,
      user
    );
  }

  public terminateEmployee(
    employeeId: string | undefined
  ): Observable<ISibasiHTTPResponse<IContact>> {
    return this.http.get<ISibasiHTTPResponse<IContact>>(
      `${environment.api_url}employees/terminate/${employeeId}`
    );
  }

  public activateEmployee(
    employeeId: string | undefined
  ): Observable<ISibasiHTTPResponse<any>> {
    return this.http.get<ISibasiHTTPResponse<any>>(
      `${environment.api_url}employees/onboard/${employeeId}`
    );
  }
  public getAllOpportunities(): Observable<ISibasiHTTPResponse<any[]>> {
    return this.http.get<ISibasiHTTPResponse<any[]>>(
      `${environment.api_url}contactOpportunity/all`
    );
  }

  //  *********************************************INSURER SERVICE*******************************************************

  public getAllInsurers(): Observable<ISibasiHTTPResponse<IContact[]>> {
    return this.http.get<ISibasiHTTPResponse<IContact[]>>(
      `${environment.api_url}insurer`
    );
  }
  public addInsurer(insurer: any): Observable<ISibasiHTTPResponse<IContact>> {
    return this.http.post<ISibasiHTTPResponse<IContact>>(
      `${environment.api_url}insurer`,
      insurer
    );
  }
  public getInsurerById(
    insurerId: string | undefined
  ): Observable<ISibasiHTTPResponse<IContact>> {
    return this.http.get<ISibasiHTTPResponse<IContact>>(
      `${environment.api_url}insurer/${insurerId}`
    );
  }
  public updateInsurer(
    insurerId: string | undefined,
    insurer: {}
  ): Observable<ISibasiHTTPResponse<IContact>> {
    return this.http.post<ISibasiHTTPResponse<IContact>>(
      `${environment.api_url}insurer/${insurerId}`,
      insurer
    );
  }
  public deleteInsurer(insurerId: string): Observable<ISibasiHTTPResponse<IContact>> {
    return this.http.delete<ISibasiHTTPResponse<IContact>>(
      `${environment.api_url}insurer/${insurerId}`
    );
  }
  getAllInsurersCount(): Observable<ISibasiHTTPResponse<number>> {
    return this.http.get<ISibasiHTTPResponse<number>>(
      `${environment.api_url}insurer/all/count`
    );
  }
  linkInsurerToContact(
    insurerId: string | undefined,
    contactId: string | undefined
  ): Observable<ISibasiHTTPResponse<IContact>> {
    return this.http.get<ISibasiHTTPResponse<IContact>>(
      `${environment.api_url}insurer/link/${insurerId}/${contactId}`
    );
  }

  getAllSearchedInsurersCount(
    searchParam: string
  ): Observable<ISibasiHTTPResponse<number>> {
    return this.http.post<ISibasiHTTPResponse<number>>(
      `${environment.api_url}insurer/all/count/searched`,
      { searchParam }
    );
  }

  getPaginatedInsurers(
    pagination?: any
  ): Observable<ISibasiHTTPResponse<IPagination<IContact[]>>> {
    return this.http.post<ISibasiHTTPResponse<IPagination<IContact[]>>>(
      `${environment.api_url}insurer/all/paginated`,
      pagination ? pagination : {}
    );
  }

  public addBulkInsurers(items: []): Observable<ISibasiHTTPResponse<any>> {
    return this.http.post<ISibasiHTTPResponse<any>>(
      `${environment.api_url}insurer/bulk/add`,
      items
    );
  }
}
